// @/hooks/useContentScmInvite.js
import { useStaticQuery, graphql } from 'gatsby';
import { useLanguage } from '@/components/providers/LanguageContext';
import { useState, useEffect } from 'react';
import { contentStore } from '@/services/app/content';

/**
 * Hook to fetch SCM Invite Drawer content from Gatsby's GraphQL layer
 * Uses language context to automatically update content when language changes
 * 
 * @param {string} [fallbackLocale='en'] - Fallback locale if context not available or locale not found
 * @returns {Object|null} Content for requested locale or null if no content exists at all
 */
export const useContentScmInvite = (fallbackLocale = 'en') => {
  // Get current locale from context or use fallback
  const { locale } = useLanguage?.() || { locale: fallbackLocale };

  // Track content for dynamic updates
  const [content, setContent] = useState(null);

  // Execute the GraphQL query
  const data = useStaticQuery(graphql`
    query {
      allContentScmInvite {
        nodes {
          id
          drawerTitle
          tabAssignedSuppliers
          tabInviteNew
          assignedSuppliersDescription
          inviteNewDescription
          supplierNameLabel
          supplierNamePlaceholder
          supplierIdLabel
          supplierIdPlaceholder
          firstNameLabel
          firstNamePlaceholder
          lastNameLabel
          lastNamePlaceholder
          emailLabel
          emailPlaceholder
          addCcBccText
          ccEmailLabel
          ccEmailPlaceholder
          bccEmailLabel
          bccEmailPlaceholder
          industryLabel
          industryPlaceholder
          countryLabel
          countryPlaceholder
          languageLabel
          languagePlaceholder
          processingMessage
          successMessage
          noAssignedSuppliersMessage
          noAssignableSuppliersMessage
          cancelButtonText
          sendInviteButtonText
          selectInvitationTemplateButtonText
          backButtonText
          inviteSupplierButtonText
          closeButtonText
          requireFieldErrorTemplate
          invalidEmailErrorMessage
          duplicateSupplierIdError
          inviteTemplateStageSubtitle
          inviteFromCATitle
          completeInvitationText
          invitesRemainingText
          locale
        }
      }
    }
  `);

  // Update content when locale changes
  useEffect(() => {
    const nodes = data.allContentScmInvite?.nodes || [];

    // First try the requested locale
    const localeContent = nodes.find(node => node.locale === locale);

    // Fall back to English if the requested locale isn't available
    const englishContent = locale !== 'en' ? nodes.find(node => node.locale === 'en') : null;

    // Check if we have GraphQL content
    if (localeContent || englishContent) {
      setContent(localeContent || englishContent || null);
    } else {
      // Try to get content from the store as a fallback
      try {
        const storeContent = contentStore.getScmInviteContent?.(locale);
        if (storeContent) {
          setContent(storeContent);
        } else {
          console.warn(`No SCM Invite content found for locale: ${locale}`);
          setContent(null);
        }
      } catch (err) {
        console.error("Error accessing content store for SCM Invite content:", err);
        setContent(null);
      }
    }
  }, [locale, data.allContentScmInvite?.nodes]);

  // Register effect for content change events
  useEffect(() => {
    const handleContentRefresh = (event) => {
      if (event.detail?.locale) {
        // Force refresh content when an external event requests it
        try {
          const storeContent = contentStore.getScmInviteContent?.(event.detail.locale);
          if (storeContent) {
            setContent(storeContent);
          }
        } catch (err) {
          console.error("Error refreshing SCM Invite content:", err);
        }
      }
    };

    // Listen for the forceContentRefresh event
    window.addEventListener('forceContentRefresh', handleContentRefresh);

    // Clean up listener
    return () => {
      window.removeEventListener('forceContentRefresh', handleContentRefresh);
    };
  }, []);

  return content;
};

/**
 * Fallback version for components that don't have access to the language context
 * @param {string} locale - Desired locale (defaults to 'en')
 * @returns {Object|null} Content for requested locale or null
 */
export const getContentScmInviteForLocale = (locale = 'en') => {
  // First try to get content from the store
  try {
    const storeContent = contentStore.getScmInviteContent?.(locale);
    if (storeContent) {
      return storeContent;
    }
  } catch (err) {
    console.error("Error accessing content store for SCM Invite content:", err);
  }

  // Fall back to Gatsby data if store fails
  try {
    if (typeof window !== 'undefined' && window.__GATSBY_INITIAL_DATA__) {
      // Attempt to access GraphQL data from Gatsby
      const staticQueries = window.__GATSBY_INITIAL_DATA__.staticQueryResults || {};

      for (const queryId in staticQueries) {
        const queryResult = staticQueries[queryId];
        const nodes = queryResult?.data?.allContentScmInvite?.nodes || [];

        if (nodes.length > 0) {
          // First try the requested locale
          const localeContent = nodes.find(node => node.locale === locale);

          // Fall back to English if the requested locale isn't available
          const englishContent = locale !== 'en' ? nodes.find(node => node.locale === 'en') : null;

          return localeContent || englishContent || null;
        }
      }
    }
  } catch (error) {
    console.error('Error accessing GraphQL content:', error);
  }

  return null;
};